/* ─── paymentStatusHelper.ts ─────────────────────────────────────────
 * Display config + legacy helpers for contract payment_status.
 * Core calculation logic lives in contractBalance.ts → derivePaymentStatus
 * ─────────────────────────────────────────────────────────────────── */

export type PaymentStatusKey =
  | "unpaid"
  | "deposit_paid"
  | "partially_paid"
  | "paid"
  | "overpaid"
  | "refunded"
  | "cancelled";

export type PaymentStatusConfig = {
  label: string;
  icon: string;
  badge: string;
  dot: string;
};

export const PAYMENT_STATUS_CONFIG: Record<PaymentStatusKey, PaymentStatusConfig> = {
  unpaid: {
    label: "Unpaid",
    icon:  "ri-time-line",
    badge: "bg-red-400/15 text-red-400",
    dot:   "bg-red-400",
  },
  deposit_paid: {
    label: "Deposit Paid",
    icon:  "ri-coin-line",
    badge: "bg-sky-400/15 text-sky-400",
    dot:   "bg-sky-400",
  },
  partially_paid: {
    label: "Partial",
    icon:  "ri-funds-box-line",
    badge: "bg-amber-400/15 text-amber-400",
    dot:   "bg-amber-400",
  },
  paid: {
    label: "Paid",
    icon:  "ri-checkbox-circle-line",
    badge: "bg-emerald-400/15 text-emerald-400",
    dot:   "bg-emerald-400",
  },
  overpaid: {
    label: "Overpaid",
    icon:  "ri-arrow-up-circle-line",
    badge: "bg-violet-400/15 text-violet-400",
    dot:   "bg-violet-400",
  },
  refunded: {
    label: "Refunded",
    icon:  "ri-arrow-go-back-line",
    badge: "bg-slate-200 text-slate-500",
    dot:   "bg-slate-400",
  },
  cancelled: {
    label: "Cancelled",
    icon:  "ri-close-circle-line",
    badge: "bg-slate-100 text-slate-400",
    dot:   "bg-slate-300",
  },
};

/**
 * Returns display config for any status string, falling back to 'unpaid'.
 */
export function getStatusConfig(status: string | null | undefined): PaymentStatusConfig {
  return (
    PAYMENT_STATUS_CONFIG[(status ?? "unpaid") as PaymentStatusKey] ??
    PAYMENT_STATUS_CONFIG.unpaid
  );
}

/**
 * calcPaymentStatus — kept for backward compatibility.
 * Accepts confirmed/completed/paid as "paid" statuses.
 * Also handles overpaid.
 */
export function calcPaymentStatus(
  totalPaidConfirmed: number,
  totalAmount: number,
  depositAmount: number,
  currentStatus: string,
): PaymentStatusKey {
  // Never auto-override manual states
  if (currentStatus === "refunded" || currentStatus === "cancelled") {
    return currentStatus as PaymentStatusKey;
  }

  const paid    = Number(totalPaidConfirmed);
  const total   = Number(totalAmount);
  const deposit = Number(depositAmount);

  if (paid <= 0)                         return "unpaid";
  if (total > 0 && paid > total)         return "overpaid";
  if (total > 0 && paid >= total)        return "paid";
  if (deposit > 0 && paid >= deposit)    return "deposit_paid";
  if (paid > 0)                          return "partially_paid";
  return "unpaid";
}

/**
 * Groups payments by contract_id and sums confirmed/completed/paid amounts.
 */
export function buildPaidByContract(
  payments: { contract_id: string | null; amount: number; status: string }[],
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const p of payments) {
    if (!p.contract_id) continue;
    if (
      p.status !== "confirmed" &&
      p.status !== "completed" &&
      p.status !== "paid"
    ) continue;
    result[p.contract_id] = (result[p.contract_id] ?? 0) + Number(p.amount);
  }
  return result;
}